import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Input, Output, EventEmitter} from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { ImageCropperComponent, CropperSettings, Bounds } from 'ngx-img-cropper';

import { languageSupport, languageService } from '@unpispas/upp-base';

/**
 * The `UppCropComponent` is an Angular component used for cropping images with a specified crop width and height.
 * It integrates with the `ngx-img-cropper` library and supports base64 image output.
 * The component includes features such as dynamic canvas resizing and aspect ratio preservation.
 *
 * @remarks
 * This component extends the `languageSupport` class, providing support for language services.
 * Ensure the `cropHeight` and `cropWidth` inputs are valid and greater than 0.
 *
 * @selector `upp-crop-image`
 * @templateUrl `./upp-crop.html`
 * @styleUrls `./upp-crop.scss`
 *
 * @example
 * ```html
 * <upp-crop-image 
 *     [image]="imageSource" 
 *     [cropHeight]="200" 
 *     [height]="200"
 *     [cropWidth]="200" 
 *     (Cropped)="onImageCropped($event)">
 * </upp-crop-image>
 * ```
 */
@Component({
    selector: 'upp-crop-image',
    templateUrl:'./upp-crop.html',
    styleUrls: [ 
        './upp-crop.scss'
    ]
})
export class UppCropComponent extends languageSupport implements OnInit, AfterViewInit, OnDestroy {
    /**
     * Input image to be cropped.
     * Can be of any type supported by the ngx-img-cropper library.
     * @defaultValue `null`
     */    
    @Input() image: any = null;

    /**
     * The desired height of the cropped image.
     * Must be greater than 0.
     */
    @Input() cropHeight = 0;

    /**
     * The desired width of the cropped image.
     * Must be greater than 0.
     */    
    @Input() cropWidth = 0;
    
    /**
     * Visual container height in pixels for the cropper area.
     * This controls UI space and does not override the output crop size.
     * @defaultValue `400`
     */
    @Input() height = 400;
    
    /**
     * Reference to the crop box element in the template.
     * Used for dynamic sizing and rendering.
     */    
    @ViewChild('cropbox', {static: false}) _imagecropbox: ElementRef | null = null;

    /**
     * Reference to the `ngx-img-cropper` component in the template.
     * Used for interacting with the cropper API.
     */    
    @ViewChild('cropper', {static: false}) _imagecropper: ImageCropperComponent | null = null;    

    /**
     * Configuration settings for the image cropper.
     * These settings control the cropper's behavior and appearance.
     */    
    public _cropperSettings: CropperSettings | null = null;

    /**
     * Height of the cropped image in pixels.
     */    
    public _croppedHeight = 0;

    /**
     * Width of the cropped image in pixels.
     */    
    public _croppedWidth = 0;

    /**
     * Stores the cropped image data.
     */
    public _croppedImage: any = {};
        
    /**
     * Constructs the `UppCropComponent` and injects the necessary services.
     * @param lang Language service for multi-language support.
     * @param change Change detector for triggering view updates.
     */    
    constructor(private lang: languageService, private change: ChangeDetectorRef) {
        super(lang, null);
    }
    
    /**
     * Initializes the component by setting up the cropper settings and validating inputs.
     * @throws Will throw an error if `cropHeight` or `cropWidth` is not greater than 0.
     */    
    ngOnInit(){
        if (!this.cropHeight || this.cropHeight <= 0) {
            throw new Error('Height must be greater than 0');
        }
        if (!this.cropWidth || this.cropWidth <= 0) {
            throw new Error('Width must be greater than 0');
        }
    
        this._cropperSettings = new CropperSettings();
        this._cropperSettings.noFileInput = true;
        this._cropperSettings.cropOnResize = true;
        this._cropperSettings.preserveSize = false;
        this._cropperSettings.fileType = 'image/jpeg';
        this._cropperSettings.compressRatio = 0.75;

        this._cropperSettings.keepAspect = (this.cropWidth != 0) && (this.cropHeight != 0);
        if (this._cropperSettings.keepAspect){
            this._cropperSettings.croppedHeight = this._cropperSettings.height = this.cropHeight;
            this._cropperSettings.croppedWidth = this._cropperSettings.width = this.cropWidth;
        }
        
        this._cropperSettings.canvasWidth = 0;        
    }
    
    private _initialize_interval: ReturnType<typeof setInterval> | null = null;
    
    /**
     * Configures the cropper dimensions after the view is initialized.
     */    
    ngAfterViewInit(){
        this._initialize_interval = setInterval(() => {
            if ((this._imagecropbox) && (this._imagecropbox.nativeElement.clientWidth != 0)){
                // clear the interval (view already available)
                if (this._initialize_interval !== null){
                    clearInterval(this._initialize_interval);
                    this._initialize_interval = null;    
                }

                // configure the crooper with the native element
                if (this._cropperSettings){
                    this._cropperSettings.canvasWidth = this._imagecropbox.nativeElement.clientWidth;
                    this._cropperSettings.canvasHeight = this._imagecropbox.nativeElement.clientHeight;    
                }

                this.change.detectChanges();
                if (this.image && this._imagecropper){
                    this._imagecropper.setImage(this.image);
                }
            }
        }, 50);
    }
    
    /**
     * Cleans up resources when the component is destroyed.
     */    
    ngOnDestroy(){
        super.OnDestroy();

        if (this._initialize_interval !== null){
            clearInterval(this._initialize_interval);
            this._initialize_interval = null;
        }
    }
    
    
    private _bounds: Bounds | null = null;


    /**
     * Gets the current cropped image as base64 without emitting outputs.
     * @returns A base64 string of the current crop or `null` if unavailable.
     */
    public get cropped(): string | null {
        return this._cropImageToBase64(this._croppedImage['original'], this._bounds);
    }

    /**
     * Handles the crop event and stores the cropping bounds.
     * @param bounds The bounds of the cropped image.
     */    
    public OnCrop(bounds: any){
       this._bounds = bounds;
       this.CropChanged.emit(this.cropped);
    }

    /**
     * Converts the cropped image to a base64 string.
     * @param img The original image.
     * @param bounds The cropping bounds.
     * @returns A base64 string of the cropped image or `null` if conversion fails.
     * @private
     */    
    private _cropImageToBase64(img: CanvasImageSource, bounds: Bounds | null): string | null {
        const cnv = document.createElement('canvas');
        const ctx = cnv.getContext('2d');

        if (this._cropperSettings && cnv && ctx && bounds && img){
            cnv.height = Math.min(bounds.height, this.cropHeight || 150);
            cnv.width = cnv.height * (bounds.width / bounds.height);
    
            ctx.drawImage(img, bounds.left, bounds.top, bounds.width, bounds.height, 0, 0, cnv.width, cnv.height);
    
            const _type = this._cropperSettings.fileType;
            const _size = this._cropperSettings.compressRatio;
            
            return cnv.toDataURL(_type, _size);    
        }

        return null;
    }

    /**
     * Emits the cropped image in base64 format.
     */    
    @Output() Cropped = new EventEmitter<any>();      
    @Output() CropChanged = new EventEmitter<any>();

    /**
     * Emits the cropped image in base64 format to the parent component.
     */    
    public OnSave(){
        this.Cropped.emit(this.cropped);
    }
}

